import React, { useEffect, useRef, useState } from 'react';
import { CellClickedEvent } from 'ag-grid-community';
import { Link } from 'react-router-dom';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query } from 'firebase/firestore';
import firestore from '@/firebase/firestore';
import TableActions from '@/components/Table/TableActions/TableActions';
import Table from '@/components/Table/Table';
import { useConfirm } from '@/components/Confirm/ConfirmProvider';
import { useNotification } from '@/components/Notifications/NotificationProvider';
import {
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { GridOptions } from 'ag-grid-community';
import { AQLConfig } from '@/types/AQLConfig';

const AQLConfigList: React.FC = () => {
  const { showConfirm } = useConfirm();
  const { showNotification } = useNotification();

  const [configs, setConfigs] = useState<AQLConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const configId = useRef<string | null>(null);

  const getConfigs = async () => {
    const q = query(collection(firestore, 'aqlConfigs'));
    const snapshot = await getDocs(q);
    const data: AQLConfig[] = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as Omit<AQLConfig, 'id'>)
    }));
    setConfigs(data);
  };

  const deleteConfig = async (id: string) => {
    showConfirm({
      title: 'Delete AQL Config',
      description: 'Are you sure you want to delete this config? This action cannot be undone.',
      onConfirm: async () => {
        await deleteDoc(doc(firestore, 'aqlConfigs', id));
        showNotification({ text: 'Config deleted successfully', type: 'success' });
        await getConfigs();
        return true;
      },
      confirmText: 'Delete'
    });
  };

  const duplicateConfig = async (id: string) => {
    const docSnap = await getDoc(doc(firestore, 'aqlConfigs', id));
    const data = docSnap.data();
    if (data) {
      data.title = `${data.title} (copy)`;
      data.createdAt = new Date();
      data.updatedAt = new Date();
      await addDoc(collection(firestore, 'aqlConfigs'), data);
      showNotification({ text: 'Config duplicated successfully', type: 'success' });
      await getConfigs();
    }
  };

  const columnDef = [
    {
      headerName: '',
      colId: 'action',
      editable: false,
      width: 120,
      cellRenderer: (params: CellClickedEvent) => {
        configId.current = params.data.id;
        return (
          <TableActions>
            <Link to={params.data.id}>
              <button className="mx-2 text-gray-500 hover:text-primary">
                <PencilIcon className="h-4 w-4" />
              </button>
            </Link>
            <button
              onClick={() => deleteConfig(params.data.id)}
              className="mx-2 text-gray-500 hover:text-danger">
              <TrashIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => duplicateConfig(params.data.id)}
              className="mx-2 text-gray-500 hover:text-primary">
              <DocumentDuplicateIcon className="h-4 w-4" />
            </button>
          </TableActions>
        );
      }
    },
    {
      headerName: 'Title',
      field: 'title',
      sortable: true,
      filter: true,
      floatingFilter: true
    },
    {
      headerName: 'Description',
      field: 'description',
      sortable: true,
      filter: true,
      floatingFilter: true
    },
    {
      headerName: 'Complete Check',
      field: 'completeCheck',
      width: 160,
      valueGetter: (params: any) => (params.data.completeCheck ? 'Yes' : 'No')
    },
    {
      headerName: 'Thresholds',
      field: 'thresholds',
      autoHeight: true,
      valueGetter: (params: any) => `${params.data.thresholds?.length || 0} rows`
    }
  ];

  const gridOptions: GridOptions = {
    animateRows: true,
    singleClickEdit: false
  };

  useEffect(() => {
    getConfigs().then(() => setLoading(false));
  }, []);

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <div className="mb-2 flex justify-end">
        <h1 className="mb-4 mr-auto text-2xl font-bold">AQL Configurations</h1>
        <Link to="add" className="flex">
          <button
            role="button"
            className="group relative mr-2 flex items-center justify-center rounded-md border border-transparent bg-primary py-2 px-4 text-sm font-medium text-white hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
            Add new
            <PlusIcon className="ml-2 h-4 w-4" />
          </button>
        </Link>
      </div>
      <Table<AQLConfig>
        testId="aql-config-table"
        data={configs}
        columnDef={columnDef}
        gridOptions={gridOptions}
      />
    </>
  );
};

export default AQLConfigList;
